'use client';

import { useState, useEffect } from 'react';
import { Col } from 'react-bootstrap';
import BlogComponent from '../blogComponent/blogComponent';
import { imageData } from '@/config/defaultBlur';
import { SearchBlogsByTitleResult } from '@/sanity.types';
import BlogComponentSkeleton from '@/components/skeleton/blogComponentSkeleton';
import { isValidSearchQuery, sanitizeSearchTerm } from '@/util/searchUtils';

interface SearchResultsProps {
  searchQuery: string;
  onSearchComplete?: (hasResults: boolean) => void;
}

const SearchResults = ({
  searchQuery,
  onSearchComplete,
}: SearchResultsProps) => {
  const [searchResults, setSearchResults] = useState<SearchBlogsByTitleResult>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sanitizedQuery, setSanitizedQuery] = useState<string>('');

  useEffect(() => {
    const performSearch = async () => {
      // Clean up the search query
      const cleanQuery = searchQuery?.trim() || '';

      // Store the sanitized version for display purposes
      const sanitized = sanitizeSearchTerm(cleanQuery);
      setSanitizedQuery(sanitized);

      if (!cleanQuery) {
        setSearchResults([]);
        onSearchComplete?.(false);
        return;
      }

      // Use shared validation function
      if (!isValidSearchQuery(cleanQuery)) {
        setSearchResults([]);
        onSearchComplete?.(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/search/blogs?searchTerm=${encodeURIComponent(sanitized)}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch search results');
        }

        const results = await response.json();

        setSearchResults(results || []);
        onSearchComplete?.(results && results.length > 0);
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to search blogs. Please try again.');
        setSearchResults([]);
        onSearchComplete?.(false);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [searchQuery, onSearchComplete]);

  if (loading) {
    return (
      <Col lg={12}>
        <div className="d-flex justify-content-center p-4">
          <span className="visually-hidden">Searching...</span>
          <BlogComponentSkeleton />
        </div>
      </Col>
    );
  }

  if (error) {
    return (
      <Col lg={12}>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Col>
    );
  }

  if (searchQuery && searchResults.length === 0) {
    // Use shared validation function instead of duplicating logic
    const isQueryValid = isValidSearchQuery(searchQuery);

    return (
      <Col lg={12}>
        <div className="text-center p-4">
          <h3>No results found</h3>
          {isQueryValid ? (
            <p>
              No blog posts found for &quot;{sanitizedQuery || searchQuery}
              &quot;. Try different keywords.
            </p>
          ) : (
            <p>
              Please enter at least 3 characters using letters or numbers to
              search for blog posts.
            </p>
          )}
        </div>
      </Col>
    );
  }

  if (searchResults.length > 0) {
    return (
      <Col lg={12}>
        <div className="search-results-header my-3">
          <h3>Search Results for &quot;{searchQuery}&quot;</h3>
          <p className="text-muted">{searchResults.length} result(s) found</p>
        </div>
        <div
          role="region"
          aria-labelledby="search-results-section"
          className="blogCardContainer"
        >
          {searchResults.map((post) => {
            const title = post?.title;
            const blogImage =
              post?.coverMedia?.type === 'image'
                ? post?.coverMedia?.coverImage
                : post?.coverMedia?.thumbnail;
            const blogImageAlt =
              post?.coverMedia?.type === 'image'
                ? post?.coverMedia?.coverImage?.alt || ''
                : post?.coverMedia?.thumbnail?.alt || '';

            return (
              <BlogComponent
                src={blogImage || '/n_images/default-blog.jpg'}
                alt={blogImageAlt}
                blogTitle={title}
                href={`/blog/${post?.slug?.current}`}
                key={post?._id}
                blurDataURL={
                  post?.coverMedia?.coverImage?.imgAsset?.metadata?.lqip ||
                  post?.coverMedia?.thumbnail?.imgAsset?.metadata?.lqip ||
                  imageData.blurImage
                }
              />
            );
          })}
        </div>
      </Col>
    );
  }

  return null;
};

export default SearchResults;
